import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MapPin, Clock, Star, Award, FileText, BadgeCheck, Trophy, BookOpen } from "lucide-react";
import { openRazorpayCheckout } from "../lib/razorpay";

export default function TeacherDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teacher, setTeacher] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingId, setBookingId] = useState(null);
  const [demoStatus, setDemoStatus] = useState({ booked: false });
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoPrice, setDemoPrice] = useState(99);
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState("");
  const [courses, setCourses] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [enrolling, setEnrolling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s, p] = await Promise.all([
        api.get(`/teachers/${id}`),
        api.get(`/teachers/${id}/availability`),
        api.get("/demo/price"),
      ]);
      setTeacher(t.data);
      setSlots(s.data);
      setDemoPrice(p.data.price || 99);
      // Courses + reviews (best-effort)
      api.get(`/teachers/${id}/courses`).then((r) => setCourses(r.data || [])).catch(() => {});
      api.get(`/teachers/${id}/reviews`).then((r) => {
        // Iter34: shape is {reviews, total, ...} for gated access; fall back to array.
        const d = r.data;
        setReviews(Array.isArray(d) ? d : (d.reviews || []));
      }).catch(() => {});
      if (user?.role === "parent") {
        try {
          const ds = await api.get(`/demo/status/${id}`);
          setDemoStatus(ds.data);
        } catch {}
        try {
          const pp = await api.get("/parent/profile");
          const kids = pp.data?.children || [];
          setChildren(kids);
          if (kids.length > 0) setSelectedChildId((prev) => prev || kids[0].id);
        } catch {}
      }
    } catch {
      toast.error("Could not load mentor");
    } finally {
      setLoading(false);
    }
  }, [id, user?.role]);

  useEffect(() => {
    load();
  }, [load]);

  const book = async (slot) => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "parent") { toast.error("Only explorers can book"); return; }
    setBookingId(slot.id);
    try {
      await api.post("/bookings", {
        teacher_id: id,
        slot_id: slot.id,
        child_id: selectedChildId || undefined,
      });
      toast.success("Booking request sent — waiting for mentor approval");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBookingId(null);
    }
  };

  const bookDemo = async () => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "parent") { toast.error("Only explorers can book a demo"); return; }
    setDemoLoading(true);
    try {
      const { data } = await api.post("/demo/create-order", {
        teacher_id: id,
        child_id: selectedChildId || undefined,
      });
      openRazorpayCheckout({
        orderId: data.order_id,
        amount: data.amount,
        currency: data.currency,
        keyId: data.key_id,
        name: "Rate-ED",
        description: `Demo Class · ${teacher.name}`,
        prefill: { name: user.name, email: user.email },
        onSuccess: async (response) => {
          try {
            await api.post("/demo/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Demo booked! The mentor will reach out via the app.");
            await load();
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Verification failed");
          } finally {
            setDemoLoading(false);
          }
        },
        onDismiss: () => {
          setDemoLoading(false);
        },
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setDemoLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-teal" data-testid="teacher-loading">Loading…</div>;
  }
  if (!teacher) return null;

  return (
    <div className="bg-peach min-h-screen" data-testid="teacher-detail-page">
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white rounded-3xl p-6 border border-black/5">
          <div className="flex items-center gap-4">
            <div className="relative">
              {teacher.photo_path ? (
                <img
                  src={`${API}/files/${teacher.photo_path}`}
                  alt={teacher.name}
                  loading="lazy"
                  decoding="async"
                  className="h-20 w-20 rounded-full object-cover border border-black/10 select-none pointer-events-none"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  data-testid="teacher-photo"
                />
              ) : (
                <div
                  className="h-20 w-20 rounded-full flex items-center justify-center font-display font-bold text-3xl"
                  style={{ background: "#FFD708", color: "#13A699" }}
                  data-testid="teacher-avatar"
                >
                  {(teacher.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              {teacher.approval_status === "approved" && (
                <span
                  title="Verified by Rate-ED"
                  className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-md"
                  data-testid="teacher-verified-badge"
                >
                  <BadgeCheck className="h-6 w-6" fill="#13A699" stroke="#FFF7ED" strokeWidth={2.5} />
                </span>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: "#13A699" }} data-testid="teacher-name">
                  {teacher.name}
                </h1>
                {teacher.approval_status === "approved" && (
                  <span
                    className="text-[11px] font-bold uppercase px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                    style={{ background: "#13A699", color: "#FFF7ED" }}
                    data-testid="teacher-verified-pill"
                  >
                    <BadgeCheck className="h-3.5 w-3.5" /> Verified by Rate-ED
                  </span>
                )}
                {teacher.verified_professional && (
                  <span
                    className="text-[11px] font-bold uppercase px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                    style={{ background: "#3B82F6", color: "#FFFFFF" }}
                    data-testid="teacher-pro-verified-pill"
                    title="Verified Professional Mentor"
                  >
                    <BadgeCheck className="h-3.5 w-3.5" /> Pro Verified
                  </span>
                )}
              </div>
              <div className="text-sm flex items-center gap-1" style={{ color: "#13A699" }}>
                <MapPin className="h-4 w-4" /> {teacher.location || "—"}
              </div>
              <div className="text-sm flex items-center gap-1 mt-1" style={{ color: "#13A699" }}>
                <Clock className="h-4 w-4" /> {teacher.experience_years} yrs experience
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <Stat label="Fees" value={`₹${teacher.fees_per_hour}/hr`} />
            <Stat label="Rating" value={`${teacher.rating_avg ? teacher.rating_avg.toFixed(1) : "—"} (${teacher.rating_count})`} />
            <Stat label="Subjects" value={(teacher.subjects || []).length} />
          </div>

          {/* Per-attribute rating breakdown (post course-completion feedback) */}
          {teacher.rating_breakdown && (
            <div className="mt-4 rounded-2xl border border-black/10 p-4 bg-white/60" data-testid="rating-breakdown">
              <h3 className="font-display text-sm font-bold uppercase tracking-wide mb-3" style={{ color: "#13A699" }}>
                Explorer feedback by attribute
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[
                  { key: "punctuality",  label: "Punctuality" },
                  { key: "fluency",      label: "Fluency of language" },
                  { key: "content",      label: "Content quality" },
                  { key: "availability", label: "Availability" },
                ].map(({ key, label }) => {
                  const b = teacher.rating_breakdown[key] || {};
                  const avg = b.avg;
                  const count = b.count || 0;
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between rounded-xl px-3 py-2"
                      style={{ background: "#FFF7ED" }}
                      data-testid={`rating-breakdown-${key}`}
                    >
                      <span className="font-semibold" style={{ color: "#13A699" }}>{label}</span>
                      <span className="text-xs font-bold" style={{ color: "#13A699" }}>
                        {avg != null ? `${Number(avg).toFixed(1)} ★` : "—"}
                        <span className="opacity-60"> ({count})</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Link to dedicated Performance page */}
          <Link
            to={`/teacher/${teacher.id}/performance`}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-2xl font-bold text-sm border-2 hover:bg-[#13A699] hover:text-white transition"
            style={{ borderColor: "#13A699", color: "#13A699" }}
            data-testid="view-performance-link"
          >
            <Trophy className="h-4 w-4" /> View Performance &amp; Badges →
          </Link>

          <h2 className="font-display text-xl font-bold mt-6" style={{ color: "#13A699" }}>Bio</h2>
          <p className="mt-1 text-sm" style={{ color: "#13A699" }}>{teacher.bio || "No bio yet."}</p>

          <h2 className="font-display text-xl font-bold mt-5" style={{ color: "#13A699" }}>Qualifications</h2>
          <p className="mt-1 text-sm flex items-start gap-2" style={{ color: "#13A699" }}>
            <Award className="h-4 w-4 mt-0.5" /> {teacher.qualifications || "—"}
          </p>
          {teacher.degree_path && (
            <a
              href={`${API}/files/${teacher.degree_path}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase px-3 py-1.5 rounded-full border border-[#13A699] hover:bg-[#13A699] hover:text-white transition-colors"
              style={{ color: "#13A699" }}
              data-testid="view-degree-btn"
            >
              <FileText className="h-3.5 w-3.5" /> View Degree
            </a>
          )}

          {teacher.teaching_locations?.length > 0 && (
            <>
              <h2 className="font-display text-xl font-bold mt-5" style={{ color: "#13A699" }}>Mentoring Location</h2>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="teacher-teaching-locations">
                {teacher.teaching_locations.map((loc) => (
                  <span
                    key={loc}
                    className="text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                    style={{ background: "#FFD708", color: "#13A699" }}
                    data-testid={`teaching-location-${loc}`}
                  >
                    <MapPin className="h-3 w-3" />
                    {loc === "explorer_home" ? "Explorer home visit" : "Mentor location visit"}
                  </span>
                ))}
              </div>
            </>
          )}

          {teacher.subjects?.length > 0 && (
            <>
              <h2 className="font-display text-xl font-bold mt-5" style={{ color: "#13A699" }}>Teaches</h2>
              <div className="mt-2 flex flex-wrap gap-2">
                {teacher.subjects.map((s) => (
                  <span key={s} className="text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: "#FFF7ED", color: "#13A699", border: "1px solid #13A69922" }}>{s}</span>
                ))}
              </div>
            </>
          )}

          {/* Courses offered */}
          {courses.length > 0 && (
            <>
              <h2 className="font-display text-xl font-bold mt-6 inline-flex items-center gap-2" style={{ color: "#13A699" }}>
                <BookOpen className="h-5 w-5" /> Courses Offered
              </h2>
              <ul className="mt-3 space-y-2" data-testid="courses-offered">
                {courses.map((c) => (
                  <li
                    key={c.id}
                    className="rounded-2xl border border-black/5 p-3 flex flex-wrap items-center justify-between gap-2 bg-[#FFF7ED]"
                    data-testid={`course-offer-${c.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-display font-bold" style={{ color: "#13A699" }}>{c.title}</div>
                      <div className="text-xs" style={{ color: "#13A69988" }}>
                        {c.subject || "—"} • {c.num_sessions} sessions • {c.duration_per_session_min} min each
                      </div>
                      {c.description && <p className="text-xs mt-1" style={{ color: "#13A699" }}>{c.description}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-display font-bold" style={{ color: "#13A699" }}>₹{c.fees}</span>
                      {user?.role === "parent" && (
                        <button
                          onClick={async () => {
                            setEnrolling(c.id);
                            try {
                              // Step 1: create Razorpay order for this course
                              const { data: order } = await api.post(
                                "/parent/courses/enroll/create-order",
                                { course_id: c.id, child_id: selectedChildId || null },
                              );
                              // Step 2: open Razorpay checkout
                              await new Promise((resolve, reject) => {
                                openRazorpayCheckout({
                                  orderId: order.order_id,
                                  amount: order.amount,
                                  currency: order.currency,
                                  keyId: order.key_id,
                                  description: `Course: ${order.course_title}`,
                                  prefill: { name: user?.name || "", email: user?.email || "" },
                                  onSuccess: async (response) => {
                                    try {
                                      await api.post("/parent/courses/enroll/verify", {
                                        razorpay_order_id: response.razorpay_order_id,
                                        razorpay_payment_id: response.razorpay_payment_id,
                                        razorpay_signature: response.razorpay_signature,
                                      });
                                      toast.success(`Enrolled in "${c.title}"`);
                                      resolve();
                                    } catch (err) {
                                      toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed");
                                      reject(err);
                                    }
                                  },
                                  onDismiss: () => {
                                    toast.message("Payment cancelled.");
                                    resolve();
                                  },
                                });
                              });
                            } catch (err) {
                              toast.error(formatApiError(err.response?.data?.detail) || "Failed to start payment");
                            } finally {
                              setEnrolling(null);
                            }
                          }}
                          disabled={enrolling === c.id}
                          className="text-xs font-bold px-3 py-1.5 rounded-xl bg-[#13A699] text-white hover:opacity-90"
                          data-testid={`enroll-course-${c.id}`}
                        >
                          {enrolling === c.id ? "Processing…" : `Pay ₹${c.fees} & Enroll`}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Reviews — combined course & session ratings, IMMUTABLE */}
          <div className="mt-6 flex items-baseline justify-between gap-3">
            <h2 className="font-display text-xl font-bold" style={{ color: "#13A699" }}>Reviews</h2>
            {reviews.length > 3 && (
              <Link
                to={`/teacher/${teacher.id}/reviews`}
                className="text-xs font-bold underline"
                style={{ color: "#13A699" }}
                data-testid="see-all-reviews-link"
              >
                See all {reviews.length} reviews →
              </Link>
            )}
          </div>
          {reviews.length === 0 ? (
            <p className="mt-1 text-sm" style={{ color: "#13A699" }}>No reviews yet.</p>
          ) : (
            <div className="mt-2 space-y-3" data-testid="reviews-list">
              {reviews.slice(0, 3).map((r, idx) => (
                <div key={idx} className="rounded-xl border border-black/5 p-3" data-testid={`review-${idx}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Star className="h-4 w-4" fill="#FFD708" stroke="#13A699" />
                    <span className="font-bold text-sm" style={{ color: "#13A699" }}>{r.rating}/5</span>
                    <span className="text-xs" style={{ color: "#13A69988" }}>by {r.parent_name}</span>
                    {r.kind === "course" && r.course_title && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: "#FFD708", color: "#13A699" }}>
                        Course: {r.course_title}
                      </span>
                    )}
                  </div>
                  {r.review && <p className="text-sm mt-1" style={{ color: "#13A699" }}>{r.review}</p>}
                </div>
              ))}
              {reviews.length > 3 && (
                <Link
                  to={`/teacher/${teacher.id}/reviews`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl font-bold text-sm border-2 hover:bg-[#13A699] hover:text-white transition"
                  style={{ borderColor: "#13A699", color: "#13A699" }}
                  data-testid="read-all-reviews-btn"
                >
                  <Star className="h-4 w-4" /> Read all reviews →
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Booking panel */}
        <div className="bg-white rounded-3xl p-6 border border-black/5 h-fit md:sticky md:top-24" data-testid="booking-panel">
          {/* Child picker — only shown for explorers */}
          {user?.role === "parent" && (
            <div className="rounded-2xl p-4 mb-3 border border-black/10 bg-white" data-testid="child-picker-card">
              <label className="text-xs font-bold uppercase" style={{ color: "#13A699" }}>
                Booking for which explorer?
              </label>
              {children.length === 0 ? (
                <p className="text-xs mt-2" style={{ color: "#13A699AA" }}>
                  No children added yet. <Link to="/parent/dashboard?tab=profile" className="underline font-bold" style={{ color: "#13A699" }}>Add a child</Link> to personalise this booking.
                </p>
              ) : (
                <select
                  value={selectedChildId}
                  onChange={(e) => setSelectedChildId(e.target.value)}
                  className="w-full mt-1 rounded-xl border border-black/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
                  data-testid="child-picker-select"
                >
                  {children.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.grade ? ` · ${c.grade}` : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Demo Class upsell */}
          <div
            className="rounded-2xl p-4 mb-5 border-2"
            style={{ background: "#FFD70822", borderColor: "#FFD708" }}
            data-testid="demo-class-card"
          >
            <h3 className="font-display font-bold uppercase text-base tracking-wide" style={{ color: "#13A699" }}>
              Try a 15-min Demo
            </h3>
            <p className="text-xs mt-1" style={{ color: "#13A699" }}>
              Meet the mentor first. One-time ₹{demoPrice} per mentor.
            </p>
            {demoStatus.booked ? (
              <span
                className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase px-3 py-1.5 rounded-full"
                style={{ background: "#FFD708", color: "#13A699" }}
                data-testid="demo-booked-badge"
              >
                Demo Booked ✓
              </span>
            ) : (
              <button
                onClick={bookDemo}
                disabled={demoLoading}
                className="btn-yellow mt-3 w-full py-2 text-sm disabled:opacity-60"
                data-testid="book-demo-btn"
              >
                {demoLoading ? "Opening…" : `Book Demo · ₹${demoPrice}`}
              </button>
            )}
          </div>

          <h2 className="font-display text-xl font-bold uppercase" style={{ color: "#13A699" }}>Available Slots</h2>
          {slots.length === 0 ? (
            <p className="mt-2 text-sm" style={{ color: "#13A699" }}>No upcoming slots.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {slots.map((s) => (
                <li key={s.id} className="rounded-xl border border-black/5 p-3 flex items-center justify-between" data-testid={`slot-${s.id}`}>
                  <div className="text-sm" style={{ color: "#13A699" }}>
                    <div className="font-bold">{s.date}</div>
                    <div className="text-xs">{s.start_time} – {s.end_time}</div>
                    <div className="text-xs mt-0.5">{s.fully_booked ? "0 slots" : `${s.slots_left} slots left`}</div>
                  </div>
                  {s.fully_booked ? (
                    <button
                      disabled
                      className="text-xs font-bold uppercase px-3 py-2 rounded-full bg-black/10 text-black/50 cursor-not-allowed"
                      data-testid={`slot-fully-booked-${s.id}`}
                    >
                      Fully Booked
                    </button>
                  ) : (
                    <button
                      onClick={() => book(s)}
                      disabled={bookingId === s.id}
                      className="btn-yellow text-xs font-bold uppercase px-3 py-2 disabled:opacity-60"
                      data-testid={`slot-book-${s.id}`}
                    >
                      {bookingId === s.id ? "Booking..." : "Book"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {!user && (
            <Link to="/login" className="btn-outline-teal w-full mt-4 py-2 text-sm" data-testid="login-to-book-link">
              Login to Book
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-[#FFF7ED] p-3 border border-black/5">
      <div className="text-[10px] uppercase font-bold tracking-widest" style={{ color: "#13A699" }}>{label}</div>
      <div className="font-display font-bold text-lg" style={{ color: "#13A699" }}>{value}</div>
    </div>
  );
}

